import type { ComponentType, SVGProps } from 'react';
import {
  ArrowsRightLeftIcon,
  BeakerIcon,
  CircleStackIcon,
  HeartIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';

export type TabKey = 'GDA' | 'DDA' | 'VDA' | 'DISEASES' | 'GENES' | 'VARIANTS' | 'CHEMICALS';

export type TabDisplayInfo = {
  label: string;
  description: string;
  associationType: string;
  helpSectionId?: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

export const TAB_DISPLAY_INFO: Record<TabKey, TabDisplayInfo> = {
  GDA: {
    label: 'Gene–Disease',
    description: 'Gene-disease associations',
    associationType: 'gene–disease associations',
    helpSectionId: 'showGDA',
    Icon: ArrowsRightLeftIcon,
  },
  DDA: {
    label: 'Drug–Disease',
    description: 'Drug-disease associations',
    associationType: 'drug–disease associations',
    helpSectionId: 'showDDA',
    Icon: ArrowsRightLeftIcon,
  },
  VDA: {
    label: 'Variant–Disease',
    description: 'Variant-disease associations',
    associationType: 'variant–disease associations',
    helpSectionId: 'showVDA',
    Icon: PuzzlePieceIcon,
  },
  DISEASES: {
    label: 'Diseases',
    description: 'Disease catalog',
    associationType: 'disease catalog entries',
    Icon: HeartIcon,
  },
  GENES: {
    label: 'Genes',
    description: 'Gene catalog',
    associationType: 'gene catalog entries',
    Icon: CircleStackIcon,
  },
  VARIANTS: {
    label: 'Variants',
    description: 'Variant catalog',
    associationType: 'variant catalog entries',
    Icon: PuzzlePieceIcon,
  },
  CHEMICALS: {
    label: 'Chemicals',
    description: 'Chemical catalog',
    associationType: 'chemical catalog entries',
    Icon: BeakerIcon,
  },
};

const SOURCE_DISPLAY_NAMES: Record<string, string> = {
  CHEMBL: 'CHEMBL',
  CLINGEN: 'CLINGEN',
  CLINVAR: 'CLINVAR',
  CLINPGX: 'CLINPGX',
  GWASCAT: 'GWASCAT',
  PHEWASCAT: 'PHEWASCAT',
  UKBIOBANK: 'UKBIOBANK',
  FINNGEN: 'FINNGEN',
  MGD_MOUSE: 'MGD_MOUSE',
  MGD_HUMAN: 'MGD_HUMAN',
  RGD_RAT: 'RGD_RAT',
  RGD_HUMAN: 'RGD_HUMAN',
  TEXTMINING_HUMAN: 'TEXTMINING_HUMAN',
  TEXTMINING_MODELS: 'TEXTMINING_MODELS',
  CLINICALTRIALS: 'CLINICALTRIALS',
  UNIPROT: 'UNIPROT',
  PSYGENET: 'PSYGENET',
  ORPHANET: 'ORPHANET',
  GENCC: 'GENCC',
  HPO: 'HPO',
};

export function getTabDisplayInfo(tab: string): TabDisplayInfo {
  return (
    TAB_DISPLAY_INFO[tab as TabKey] ?? {
      label: tab,
      description: tab,
      associationType: `${tab.toLowerCase()} data`,
      Icon: CircleStackIcon,
    }
  );
}

export function getSourceDisplayName(source: string): string {
  if (SOURCE_DISPLAY_NAMES[source]) {
    return SOURCE_DISPLAY_NAMES[source];
  }
  return source.replace(/_/g, ' ');
}
